// Session: the instance is chosen at login (name like `michael`, or a URL for local dev). The app asks the instance
// `GET /.well-known/truss` → {instance, auth}, then logs in at that auth with a PAT (auth/contracts: POST
// /session/login {pat} → access JWT ~15 min + refresh token). Every call carries the access token; a 401 refreshes
// once (POST /session/refresh) and retries. Without an auth issuer (a local truss alone) the PAT itself is the bearer.
// Kept in localStorage: instance, api, auth, refresh, access (+ expiry), me.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ApiError, api, configure, type Person, resetKit, USE_MOCKS } from "@/api";
import { API_BASE_DEFAULT } from "@/api/client";

const START = USE_MOCKS ? "mock" : null;

type Creds = { instance: string; api: string; auth: string | null; access: string; refresh: string | null; exp: number };

type AuthState = {
	/** the bearer in use (access JWT or PAT); null = signed out */
	token: string | null;
	instance: string | null;
	creds: Creds | null;
	me: Person | null;
	error: string | null;
	busy: boolean;
	signIn: (instance: string, pat: string) => Promise<boolean>;
	/** The instance's own browser session (a passkey sign-in on its /auth pages) → this app's session; false = none. */
	signInBrowser: () => Promise<boolean>;
	signOut: () => void;
};

/** A URL or localhost[:port] → the instance's API base. */
export const apiBaseOf = (v: string) => {
	const s = v.trim();
	if (/^https?:\/\//.test(s)) return s.replace(/\/+$/, "");
	if (/^(localhost|127\.0\.0\.1)(:\d+)?$/.test(s)) return `http://${s}`;
	// a bare name says nothing about where the instance runs (custom domain, self-hosted)
	throw new Error(`${s}: type the instance's address (https://\u2026)`);
};

let refreshing: Promise<string | null> | null = null;
/** When the last refresh landed: refused again right after it, the session is gone (never a refresh loop). */
let refreshedAt = 0;
const AGAIN_MS = 30_000;

// Several tabs of one origin share one session: localStorage holds it, each tab its own copy in memory. Auth rotates
// the refresh token on every use, so a tab that refreshed with a token another tab already rotated was refused (and
// signed out, revoking the session for all). Hence: one refresh at a time across tabs (a Web Lock), and inside it the
// stored session first: another tab's newer token is taken, not rotated again; a tab follows the others' sign-in and
// sign-out (the storage event).
const KEY = "hq.auth";
/** The session as stored (by any tab); null = signed out there. */
function stored(): Creds | null {
	try {
		return (JSON.parse(localStorage.getItem(KEY) ?? "null") as { state?: { creds?: Creds | null } } | null)?.state?.creds ?? null;
	} catch {
		return null;
	}
}
/** Another tab's newer tokens of the same session, into this tab. */
function adopt(c: Creds) {
	useAuth.setState({ creds: c, token: c.access });
	configure({ base: c.api, auth: c.auth, token: c.access });
}
/** Signed out in another tab (or its session refused): this tab follows, without revoking anything itself. */
function dropLocal() {
	configure({ token: null, person: null });
	resetKit();
	useAuth.setState({ token: null, creds: null, me: null, error: null });
}
const acrossTabs = <T>(f: () => Promise<T>): Promise<T> =>
	typeof navigator !== "undefined" && navigator.locks ? navigator.locks.request("hq.auth.refresh", f) : f();

/** New access token from the refresh token (one at a time, across tabs); null = signed out. */
export const refreshToken = () => refresh();
async function refresh(): Promise<string | null> {
	const c = useAuth.getState().creds;
	// nothing to renew it with (a PAT as the bearer): the credential is gone, back to login (401 = stop)
	if (!c?.auth || !c.refresh) {
		if (useAuth.getState().token) useAuth.getState().signOut();
		return null;
	}
	if (!refreshing && Date.now() - refreshedAt < AGAIN_MS) {
		dropLocal();
		return null;
	}
	refreshing ??= acrossTabs(async () => {
		const mine = useAuth.getState().creds;
		if (!mine?.auth || !mine.refresh) return null;
		const s = stored();
		if (!s) {
			dropLocal(); // signed out in another tab meanwhile
			return null;
		}
		if (s.auth === mine.auth && s.refresh && s.refresh !== mine.refresh) {
			adopt(s); // another tab rotated it: its fresh access token is this tab's too
			refreshedAt = Date.now();
			return s.access;
		}
		try {
			const a = await api.auth.refresh(mine.auth, mine.refresh);
			const creds = { ...mine, access: a.access, refresh: a.refresh, exp: Date.now() + a.expiresIn * 1000 };
			useAuth.setState({ creds, token: creds.access }); // stored at once (persist), before the lock is let go
			configure({ base: mine.api, auth: mine.auth, token: creds.access }); // the kit takes the fresh token from here
			refreshedAt = Date.now();
			return creds.access;
		} catch (e) {
			// refused: the session is gone (its token is not this tab's to revoke: another tab may hold a newer one of
			// it), back to login; offline: keep the session (the kit stays stopped until a fresh token)
			if (e instanceof ApiError && (e.status === 401 || e.status === 400)) dropLocal();
			return null;
		}
	}).finally(() => {
		refreshing = null;
	});
	return refreshing;
}

export const useAuth = create<AuthState>()(
	persist(
		(set, get) => ({
			token: START,
			instance: USE_MOCKS ? "michael" : null,
			creds: null,
			me: null,
			error: null,
			busy: false,
			async signIn(instanceInput, raw) {
				const pat = raw.trim();
				if (!pat || !instanceInput.trim()) return false;
				set({ busy: true, error: null });
				try {
					if (USE_MOCKS) {
						const me = await api.session.me(pat);
						set({ token: pat, me, busy: false, instance: instanceInput.trim() });
						return true;
					}
					const base = apiBaseOf(instanceInput);
					const known = await api.auth.discover(base);
					const instance = known.instance ?? instanceInput.trim();
					const auth = known.auth;
					let creds: Creds = { instance, api: base, auth, access: pat, refresh: null, exp: 0 };
					if (auth) {
						const a = await api.auth.login(auth, pat);
						creds = { ...creds, access: a.access, refresh: a.refresh, exp: Date.now() + a.expiresIn * 1000 };
					}
					configure({ base, auth, token: creds.access });
					const me = await api.session.me(creds.access);
					configure({ instance, person: me.id });
					set({ token: creds.access, creds, instance, me, busy: false });
					return true;
				} catch (e) {
					set({ busy: false, error: e instanceof ApiError && e.unauthorized ? "Invalid token" : errorText(e) });
					return false;
				}
			},
			async signInBrowser() {
				if (USE_MOCKS) return false;
				const base = API_BASE_DEFAULT;
				try {
					const known = await api.auth.discover(base);
					const auth = known.auth;
					if (!auth || new URL(auth).origin !== location.origin) return false;
					const a = await api.auth.browser(auth);
					if (!a) return false;
					const instance = known.instance ?? "";
					const creds: Creds = { instance, api: base, auth, access: a.access, refresh: a.refresh, exp: Date.now() + a.expiresIn * 1000 };
					configure({ base, auth, token: creds.access });
					const me = await api.session.me(creds.access);
					configure({ instance, person: me.id });
					set({ token: creds.access, creds, instance, me, error: null });
					return true;
				} catch {
					return false;
				}
			},
			signOut() {
				const c = get().creds;
				if (c?.auth && c.refresh) void api.auth.logout(c.auth, c.refresh).catch(() => {});
				configure({ token: null, person: null });
				resetKit();
				set({ token: null, creds: null, me: null, error: null });
			},
		}),
		{
			name: "hq.auth",
			partialize: (s) => ({ token: s.token, me: s.me, creds: s.creds, instance: s.instance }),
			onRehydrateStorage: () => (s) => {
				if (s?.creds) configure({ base: s.creds.api, auth: s.creds.auth, token: s.token, instance: s.creds.instance, person: s.me?.id ?? null });
				else configure({ token: s?.token ?? null });
				// a session kept for another address than the configured one (the dev server signed in elsewhere, or an
				// instance that moved): if that address no longer answers, the session is dropped and sign-in starts at
				// the configured base, never a page of failing calls to a dead origin
				const stale = s?.creds && !USE_MOCKS && s.creds.api !== API_BASE_DEFAULT ? s.creds.api : null;
				if (stale)
					void api.auth.discover(stale).catch(() => {
						if (useAuth.getState().creds?.api !== stale) return;
						configure({ base: API_BASE_DEFAULT, auth: null, token: null, person: null });
						resetKit();
						useAuth.setState({ token: null, creds: null, me: null, error: null });
					});
			},
		},
	),
);

// after create(): the persisted session (rehydrated synchronously above) keeps its token
configure({ refresh, ...(USE_MOCKS ? { token: START } : {}) });

// another tab changed the stored session: a newer token of the same session is taken, a sign-out followed; a sign-in
// elsewhere (another person or instance) is picked up by a reload
if (typeof window !== "undefined" && !USE_MOCKS)
	window.addEventListener("storage", (e) => {
		if (e.key !== KEY && e.key !== null) return;
		const mine = useAuth.getState().creds;
		const s = stored();
		if (!s) {
			if (mine) dropLocal();
		} else if (mine && s.auth === mine.auth && s.instance === mine.instance) {
			if (s.refresh !== mine.refresh) adopt(s);
		} else location.reload();
	});

export const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));
